import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScheduleI, SchedulePeriods } from '~src/interfaces/schedule.interface';
import { ScheduleErrors } from '~src/modules/surveys/components/schedules/scheduleItem/templates/ScheduleTemplate';
import {
  HeaderContainer,
  PeopleSectionHeader,
  TopicDescription,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';
import Checkbox from '~src/shared/form/Checkbox';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';

import { TFunction } from 'i18next';
import { CustomCycle, CycleStatus, NewCustomCycle } from '~src/interfaces/cycle.interface';
import { CustomizedCycles, DisplayCycleStatus } from '~src/modules/surveys/containers/CustomizedCycles';
import { translateContent } from '~src/services/translationServices';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import NotificationDialog from '~src/shared/dialogs/notificationDialog/NotificationDialog';
import * as SC from './cycleContainer.sc';
import {
  ButtonContainer,
  ButtonLogo,
  CustomCycleInputsContainer,
  CycleContainerWrapper,
  CycleWrapper,
  DateInputsContainer,
  DateInputsWrapper,
  DeadlineContainer,
  DeadlineContent,
  DeadlineHeader,
  InputContainer,
  InputLabel,
  InputRow,
  InputSection,
  PeriodMessage,
  PeriodSelector,
} from './cycleContainer.sc';
import { InputLabelContent, InputLabelHeader } from './statusSection.sc';

const errorKeys = {
  startDate: 'openEnded',
  dueDate: 'deadline',
};

interface MailingFlags {
  startMail: boolean;
  repetitionMail: boolean;
  reminderMail: boolean;
  selfSelectionMail: boolean;
  managersSelectionMail: boolean;
}

const columns = (t: TFunction): ColumnsType<Partial<NewCustomCycle>> => [
  {
    title: <>{t('x.custom.cycle_name')}</>,
    width: 50,
    dataIndex: 'name',
    key: 'name',
    fixed: 'left',
  },
  {
    title: <>{t('x.surveys.cycle_section.dates.start')}</>,
    width: 50,
    dataIndex: 'startDate',
    key: 'startDate',
    fixed: 'left',
  },
  {
    title: <>{t('x.surveys.cycle_section.dates.due')}</>,
    dataIndex: 'dueDate',
    key: 'dueDate',
    width: 50,
  },
  {
    title: <>{t('x.custom.cycle.status')}</>,
    dataIndex: 'cycleStatus',
    key: 'cycleStatus',
    width: 50,
  },
];

interface Props {
  schedule: ScheduleI;
  changingState: (selectedOption: SchedulePeriods, diff?: Partial<ScheduleI>) => void;
  errors: ScheduleErrors;
  deleteError: (errorKey: string) => void;
  selectedPeriodOption: SchedulePeriods;
  isAddSchedule?: boolean;
  onChangeCycles: (cycles: Array<CustomCycle | NewCustomCycle>) => void;
  onChangeSchedule: (s: Partial<ScheduleI>) => void;
  cycles: Array<NewCustomCycle | CustomCycle>;
}

const CycleContainer: React.FC<Props> = (props) => {
  const [t] = useTranslation();
  const [isSwitchWarningOpen, setSwitchWarning] = useState(false);
  const [newCycleInterval, setNewCycleInterval] = useState(7);

  const data = props.cycles.map((cycle, index) => {
    return {
      key: index,
      name: translateContent(cycle.name ?? {}) || '-',
      startDate: cycle.startDate,
      dueDate: cycle.dueDate,
      cycleStatus: <DisplayCycleStatus status={cycle.cycleStatus} />,
    };
  });

  const hasValidCustomCycles = props.cycles.some(
    (cycle) => cycle.cycleStatus === CycleStatus.Active || cycle.cycleStatus === CycleStatus.Scheduled,
  );

  const [isCustomCyclesModalActive, setIsCustomCyclesModalActive] = useState(false);
  const scheduleEndDate =
    (props.schedule?.repetition &&
      moment(props.schedule.startDate)
        .add(props.schedule?.repetition - 1, 'd')
        .format('l')) ||
    undefined;

  const changeSelectedPeriod = (value: SchedulePeriods) => {
    if (value === SchedulePeriods.Custom && props.schedule.active && props.cycles.length === 0) {
      setSwitchWarning(true);
      return;
    }

    if (value !== SchedulePeriods.Custom && hasValidCustomCycles) return;
    props.changingState(value);
  };

  const changedRepetition = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target) {
      props.changingState(props.selectedPeriodOption, {
        repetition: parseInt(event.target.value, 10),
      });
      props.deleteError('repetition');
    }
  };

  const changedReminder = (event: ChangeEvent<HTMLInputElement>) => {
    const reminderDaysBefore = parseInt(event.target.value, 10);

    props.changingState(props.selectedPeriodOption, {
      reminder: reminderDaysBefore,
      reminderMail: Boolean(reminderDaysBefore),
    });
    props.deleteError('reminder');
  };

  const changeDate = (date: moment.Moment | null, key: 'startDate' | 'dueDate') => {
    const tmpDate = date !== null ? moment(date).format('YYYY-MM-DD') : null;
    props.changingState(props.selectedPeriodOption, { [key]: tmpDate });
    props.deleteError(errorKeys[key]);
  };

  const changeStartDate = (date: moment.Moment | null) => {
    changeDate(date, 'startDate');
  };

  const changeDueDate = (date: moment.Moment | null) => {
    changeDate(date, 'dueDate');
  };

  const changeEmailSettings = (flags: Partial<MailingFlags>) => {
    props.changingState(props.selectedPeriodOption, flags);
  };

  const changeRepetitionMailSettings = (value: boolean) => {
    changeEmailSettings({
      repetitionMail: value,
    });
  };

  const changeStartMailSettings = (value: boolean) => {
    changeEmailSettings({
      startMail: value,
      selfSelectionMail: value,
      managersSelectionMail: value,
    });
  };

  return (
    <CycleWrapper>
      <PeopleSectionHeader data-translation="x.surveys.schedule.cyclesection.header">
        <HeaderContainer>
          {t('x.surveys.schedule.cyclesection.header')}
          <TopicDescription>
            <HoverInfoIcon
              infoText={{
                key: 'x.surveys.schedule.asksection.header.info_box.cyclesection',
                txt: t('x.surveys.schedule.asksection.header.info_box.cyclesection'),
              }}
            />
          </TopicDescription>
        </HeaderContainer>
      </PeopleSectionHeader>
      <CycleContainerWrapper>
        <ButtonContainer>
          <PeriodSelector
            $active={props.selectedPeriodOption === SchedulePeriods.DeadLine}
            $disabled={hasValidCustomCycles}
            onClick={changeSelectedPeriod.bind(this, SchedulePeriods.DeadLine)}
            data-translation="x.surveys.schedule.cyclesection.buttons.deadline.title"
          >
            <ButtonLogo name="iconCircleClock" />
            <>{t('x.surveys.schedule.cyclesection.buttons.deadline.title')}</>
          </PeriodSelector>

          <PeriodMessage
            $active={props.selectedPeriodOption === SchedulePeriods.DeadLine}
            data-translation="x.surveys.schedule.cyclesection.buttons.deadline.description"
          >
            {t('x.surveys.schedule.cyclesection.buttons.deadline.description')}
          </PeriodMessage>

          <PeriodSelector
            $active={props.selectedPeriodOption === SchedulePeriods.Custom}
            onClick={() => {
              changeSelectedPeriod(SchedulePeriods.Custom);
            }}
            data-translation="x.surveys.schedule.cyclesection.buttons.custom.title"
          >
            <ButtonLogo name="iconCustomCycles" />
            <>{t('x.surveys.schedule.cyclesection.buttons.custom.title')}</>
          </PeriodSelector>

          <PeriodMessage
            $active={props.selectedPeriodOption === SchedulePeriods.Custom}
            data-translation="x.surveys.schedule.cyclesection.buttons.custom.description"
          >
            {t('x.surveys.schedule.cyclesection.buttons.custom.description')}
          </PeriodMessage>

          <PeriodSelector
            $active={props.selectedPeriodOption === SchedulePeriods.Repeated}
            $disabled={hasValidCustomCycles}
            onClick={changeSelectedPeriod.bind(this, SchedulePeriods.Repeated)}
            data-translation="x.surveys.schedule.cyclesection.buttons.repeated.title"
          >
            <ButtonLogo name="iconRepeat" />
            <>{t('x.surveys.schedule.cyclesection.buttons.repeated.title')}</>
          </PeriodSelector>

          <PeriodMessage
            $active={props.selectedPeriodOption === SchedulePeriods.Repeated}
            data-translation="x.surveys.schedule.cyclesection.buttons.repeated.description"
          >
            {t('x.surveys.schedule.cyclesection.buttons.repeated.description')}
          </PeriodMessage>

          <PeriodSelector
            $active={props.selectedPeriodOption === SchedulePeriods.OpenEnded}
            $disabled={hasValidCustomCycles}
            onClick={changeSelectedPeriod.bind(this, SchedulePeriods.OpenEnded)}
            data-translation="x.surveys.schedule.cyclesection.buttons.openEnded.title"
          >
            <ButtonLogo name="iconInfinity" />
            <>{t('x.surveys.schedule.cyclesection.buttons.openEnded.title')}</>
          </PeriodSelector>

          <PeriodMessage
            $active={props.selectedPeriodOption === SchedulePeriods.OpenEnded}
            data-translation="x.surveys.schedule.cyclesection.buttons.openEnded.description"
          >
            {t('x.surveys.schedule.cyclesection.buttons.openEnded.description')}
          </PeriodMessage>
        </ButtonContainer>

        <Dialog
          width={950}
          show={isCustomCyclesModalActive}
          onHide={() => setIsCustomCyclesModalActive(false)}
          title={<>{t('x.surveys.cyclesection.input.repetition.customize.cycle.header')}</>}
          titleKey={t('x.surveys.cyclesection.input.repetition.customize.cycle.header')}
          headerColor="white"
          headerFontColor="baseBlue"
          children={
            <CustomizedCycles
              firstCycleStartDate={moment().format('l')}
              interval={newCycleInterval}
              onChangeCycles={props.onChangeCycles}
              onChangeSchedule={props.onChangeSchedule}
              cycles={props.cycles}
              schedule={props.schedule}
              onConfirm={() => setIsCustomCyclesModalActive(false)}
            />
          }
        />

        <NotificationDialog
          t={t}
          title={{
            key: t('x.surveys.schedule.switch.restrict.title'),
            txt: t('x.surveys.schedule.switch.restrict.title'),
          }}
          descr={{
            key: t('x.surveys.schedule.switch.restrict.description'),
            txt: t('x.surveys.schedule.switch.restrict.description'),
          }}
          show={isSwitchWarningOpen}
          close
          onHide={() => setSwitchWarning(false)}
        />

        {/* INPUTS */}
        {props.selectedPeriodOption === SchedulePeriods.Custom ? (
          <CustomCycleInputsContainer $isCycleListEmpty={props.cycles.length === 0}>
            {props.cycles.length > 0 && (
              <SC.TableStyled
                columns={columns(t)}
                dataSource={data}
                scroll={{ x: 200, y: 400 }}
                bordered={false}
                pagination={{ pageSize: 5 }}
              />
            )}

            {props.cycles.length === 0 && (
              <>
                <SC.SecondaryButtonStyled
                  data-translation="x.surveys.cycle_section.add.quarterly"
                  onClick={(e) => {
                    e.preventDefault();

                    const quarters = Array.from(Array(4))
                      .map((_, index) => index + 1)
                      .map((qNum) => moment().startOf('quarter').add(qNum, 'quarter'));
                    props.onChangeCycles(
                      quarters.map((quarter) => ({
                        name: {},
                        startDate: quarter.format('YYYY-MM-DD'),
                        dueDate: quarter.add(14, 'days').format('YYYY-MM-DD'),
                        cycleStatus: CycleStatus.Scheduled,
                      })),
                    );

                    setNewCycleInterval(90);
                    setIsCustomCyclesModalActive(true);
                  }}
                >
                  {t('x.surveys.cycle_section.add.quarterly')}
                </SC.SecondaryButtonStyled>

                <SC.SecondaryButtonStyled
                  data-translation="x.surveys.cycle_section.add.monthly"
                  onClick={(e) => {
                    e.preventDefault();

                    const months = Array.from(Array(6))
                      .map((_, index) => index + 1)
                      .map((monthNum) => moment().startOf('month').add(monthNum, 'month'));

                    props.onChangeCycles(
                      months.map((month) => ({
                        name: {},
                        startDate: month.format('YYYY-MM-DD'),
                        dueDate: month.add(7, 'day').subtract(1, 'day').format('YYYY-MM-DD'),
                        cycleStatus: CycleStatus.Scheduled,
                      })),
                    );
                    setNewCycleInterval(30);
                    setIsCustomCyclesModalActive(true);
                  }}
                >
                  {t('x.surveys.cycle_section.add.monthly')}
                </SC.SecondaryButtonStyled>
              </>
            )}
            <SC.SecondaryButtonStyled
              data-translation="x.surveys.cycle_section.add.new.cycle"
              onClick={(e) => {
                e.preventDefault();
                setIsCustomCyclesModalActive(true);
              }}
            >
              {props.cycles.length > 0
                ? t('x.surveys.cycle_section.edit.cycle')
                : t('x.surveys.cycle_section.add.new.cycle')}
            </SC.SecondaryButtonStyled>
          </CustomCycleInputsContainer>
        ) : (
          <DateInputsContainer>
            <DateInputsWrapper>
              <InputSection $active>
                <InputRow $active>
                  <InputLabel>
                    <InputLabelHeader data-translation="x.surveys.schedule.cyclesection.input.startdate.title">
                      {t('x.surveys.schedule.cyclesection.input.startdate.title')}
                    </InputLabelHeader>
                    <InputLabelContent data-translation="x.surveys.schedule.cyclesection.input.startdate.description">
                      {t('x.surveys.schedule.cyclesection.input.startdate.description')}
                    </InputLabelContent>
                  </InputLabel>
                  <InputContainer>
                    <CustomDatePicker
                      error={props.errors.openEnded ? SchedulePeriods.OpenEnded : undefined}
                      dateValue={props.schedule.startDate}
                      label={t('x.surveys.schedule.cyclesection.input.fixed.header')}
                      labelKey="x.surveys.schedule.cyclesection.input.fixed.header"
                      onChange={changeStartDate}
                    />
                  </InputContainer>
                </InputRow>
                <InputRow $active>
                  <InputLabelHeader>
                    <Checkbox
                      id={`${props.schedule.id}-startMail`}
                      label={t('x.surveys.cyclesection.input.start_mail.label')}
                      labelKey={'x.surveys.cyclesection.input.start_mail.label'}
                      onChange={changeStartMailSettings}
                      isChecked={props.schedule.startMail}
                      help={t('x.surveys.cyclesection.input.start_mail.help')}
                    />
                  </InputLabelHeader>
                </InputRow>
              </InputSection>
              <InputSection $active={props.selectedPeriodOption === SchedulePeriods.Repeated}>
                <InputRow $active>
                  <InputLabel>
                    <InputLabelHeader data-translation="x.surveys.schedule.cyclesection.input.repetition.title">
                      {t('x.surveys.schedule.cyclesection.input.repetition.title')}
                    </InputLabelHeader>
                    <InputLabelContent data-translation="x.surveys.schedule.cyclesection.input.repetition.description">
                      {t('x.surveys.schedule.cyclesection.input.repetition.description')}
                    </InputLabelContent>
                  </InputLabel>
                  <InputContainer>
                    <CustomFormInput
                      type="number"
                      error={props.errors.repetition ? 'repetition' : undefined}
                      min={0}
                      label={t('x.surveys.schedule.cycle_section.input.amountdays')}
                      labelKey="x.surveys.schedule.cycle_section.input.amountdays"
                      onChange={changedRepetition}
                      value={props.schedule.repetition || ''}
                    />
                  </InputContainer>
                </InputRow>
                <InputRow $active>
                  <InputLabelHeader>
                    <Checkbox
                      id={`${props.schedule.id}-repetitionMail`}
                      label={t('x.surveys.cycle_section.input.repetition_mail.label')}
                      labelKey={'x.surveys.cycle_section.input.repetition_mail.label'}
                      onChange={changeRepetitionMailSettings}
                      isChecked={props.schedule.repetitionMail}
                      help={t('x.surveys.cyclesection.input.repetition_mail.help')}
                    />
                  </InputLabelHeader>
                </InputRow>
              </InputSection>
              <InputSection
                $active={
                  props.selectedPeriodOption === SchedulePeriods.Repeated ||
                  props.selectedPeriodOption === SchedulePeriods.DeadLine
                }
              >
                <InputRow $active>
                  <InputLabel>
                    <InputLabelHeader data-translation="x.surveys.schedule.cyclesection.input.deadline.title">
                      {t('x.surveys.schedule.cyclesection.input.deadline.title')}
                    </InputLabelHeader>
                    <InputLabelContent data-translation="x.surveys.schedule.cyclesection.input.deadline.description">
                      {t('x.surveys.schedule.cyclesection.input.deadline.description')}
                    </InputLabelContent>
                  </InputLabel>
                  <InputContainer>
                    {props.selectedPeriodOption === SchedulePeriods.Repeated ? (
                      <DeadlineContainer>
                        {props.schedule.repetition && props.schedule.startDate ? (
                          <DeadlineContent>
                            <DeadlineHeader data-translation="x.surveys.schedule.cyclesection.input.fixed.header">
                              {t('x.surveys.schedule.cyclesection.input.fixed.header')}
                            </DeadlineHeader>
                            <SC.DueDateContainer>
                              {scheduleEndDate}
                              <SC.DueDateInfoIconContainer>
                                <HoverInfoIcon infoText={t('x.surveys.cyclesection.due_date.help')} />
                              </SC.DueDateInfoIconContainer>
                            </SC.DueDateContainer>
                          </DeadlineContent>
                        ) : null}
                      </DeadlineContainer>
                    ) : (
                      <SC.DueDateContainer>
                        <CustomDatePicker
                          error={props.errors.deadline ? SchedulePeriods.DeadLine : null}
                          dateValue={props.schedule.dueDate}
                          label={t('x.surveys.schedule.cyclesection.input.fixed.header')}
                          labelKey="x.surveys.schedule.cyclesection.input.fixed.header"
                          onChange={changeDueDate}
                        />
                        <SC.DueDateInfoIconContainer>
                          <HoverInfoIcon infoText={t('x.surveys.cyclesection.due_date.help')} />
                        </SC.DueDateInfoIconContainer>
                      </SC.DueDateContainer>
                    )}
                  </InputContainer>
                </InputRow>
              </InputSection>
              <InputSection
                $active={
                  props.selectedPeriodOption === SchedulePeriods.Repeated ||
                  props.selectedPeriodOption === SchedulePeriods.DeadLine
                }
              >
                <InputRow $active>
                  <InputLabel>
                    <InputLabelHeader data-translation="x.surveys.schedule.cycle_section.input.reminder.title">
                      {t('x.surveys.schedule.cycle_section.input.reminder.title')}
                    </InputLabelHeader>
                    <InputLabelContent data-translation="x.surveys.schedule.cycle_section.input.reminder.description">
                      {t('x.surveys.schedule.cycle_section.input.reminder.description')}
                    </InputLabelContent>
                  </InputLabel>
                  <InputContainer>
                    <CustomFormInput
                      type="number"
                      error={props.errors.reminder ? 'reminder' : undefined}
                      min={0}
                      label={t('x.surveys.schedule.cycle_section.input.amountdays')}
                      labelKey="x.surveys.schedule.cycle_section.input.amountdays"
                      onChange={changedReminder}
                      value={props.schedule.reminder || ''}
                    />
                  </InputContainer>
                </InputRow>
              </InputSection>
            </DateInputsWrapper>
          </DateInputsContainer>
        )}
      </CycleContainerWrapper>
    </CycleWrapper>
  );
};

export default CycleContainer;
